<template>
  <v-container
    fluid
  >
    <image-with-border-dialog ref="imageWithBorderDialog"/>

    <ConfirmDialog
      :is-open.sync="dialogs.newAuthor.isOpened"
      confirm-button-text="Add"
      :is-disable-confirm-button="hasSomeAbilities($constants.ABILITY.INTERN)"
      @confirm="createNewAuthor"
    >
      <template v-slot:title>
        Create new author
      </template>
      <template v-slot:body>
        <v-container>
          <v-row>
            <v-text-field v-model="dialogs.newAuthor.data.name" label="name"/>
          </v-row>
          <v-row>
            <v-text-field v-model="dialogs.newAuthor.data.attribute" label="attribute"/>
          </v-row>
          <v-row>
            <v-text-field v-model="dialogs.newAuthor.data.url" label="url"/>
          </v-row>
        </v-container>
      </template>
    </ConfirmDialog>

    <ConfirmDialog
      :is-open.sync="dialogs.moveImageDialog.isOpened"
      dialog-max-width="400"
      :is-disable-confirm-button="!dialogs.moveImageDialog.data.newObjectId"
      @confirm="moveImage"
      confirm-button-text="Move"
    >
      <template v-slot:title>
        Move image {{ dialogs.moveImageDialog.data.id }}
      </template>
      <template v-slot:body>
        <v-row>
          <v-col>
            <v-text-field
              label="Old id"
              :value="objectId"
              disabled
            />
          </v-col>
          <v-col>
            <v-text-field
              label="New id"
              v-model="dialogs.moveImageDialog.data.newObjectId"
            />
          </v-col>
        </v-row>
      </template>
    </ConfirmDialog>

    <v-data-table
      :items-per-page="10"
      must-sort
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :headers="headers"
      :items="itemsComputed"
      :multi-sort="false"
    >
      <template v-slot:item.preview="{ item }">
        <v-card
          max-height="150"
          max-width="100"
          @click="$refs.imageWithBorderDialog.openDialog('image', {image: item})"
        >
          <v-img
            class="white--text align-end"
            :src="`${item.public_link}?thumb=1`"
            height="150"
            contain
          />
        </v-card>
      </template>

      <template v-slot:item.options="{ item }">
        <v-col>
          <v-row>
            <v-col
              cols="9"
              class="pa-0"
            >
              <v-edit-dialog
                @save="updateItem(itemClone, item)"
                @open="itemClone = {...item}"
                large
              >
                Author:
                <span v-if="item.author && item.author.name" class="font-weight-black">
                  {{ item.author.name }}
                </span>
                <span v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
                <template v-slot:input>
                  <div
                    class="ma-2"
                  >
                    <DataList
                      :item-model.sync="itemClone.author_id"
                      :type-object="$constants.DATA_LISTS.AUTHOR_LIST"
                      label="Author"
                      is-update-on-start
                    />
                  </div>
                </template>
              </v-edit-dialog>
            </v-col>
            <v-col
              cols="3"
              class="pa-0"
            >
              <v-btn
                x-small
                @click="openDialog('newAuthor')"
              >
                + add author
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-edit-dialog
              @save="updateItem(itemClone, item)"
              @open="itemClone = {...item}"
              large
            >
              License:
              <span v-if="item.license && item.license.name" class="font-weight-black">
                {{ item.license.name }}
              </span>
              <span v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
              <template v-slot:input>
                <div
                  class="ma-2"
                >
                  <DataList
                    :item-model.sync="itemClone.license_id"
                    :type-object="$constants.DATA_LISTS.LICENSE_LIST"
                    label="License"
                    is-update-on-start
                  />
                </div>
              </template>
            </v-edit-dialog>
          </v-row>
          <v-row>
            <v-edit-dialog
              @save="updateItem(itemClone, item)"
              @open="itemClone = {...item}"
              large
            >
              Source:
              <span v-if="item.image_source && item.image_source.name" class="font-weight-black">
                {{ item.image_source.name }}
              </span>
              <span v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
              <template v-slot:input>
                <div
                  class="ma-2"
                >
                  <DataList
                    :item-model.sync="itemClone.image_source_id"
                    :type-object="$constants.DATA_LISTS.IMAGE_SOURCE_LIST"
                    label="Source"
                    is-update-on-start
                  />
                </div>
              </template>
            </v-edit-dialog>
          </v-row>
          <v-row>
            <v-edit-dialog
              @save="updateItem(itemClone, item)"
              @open="itemClone = {...item}"
            >
              Url:
              <span v-if="item.url" class="font-weight-black">{{ item.url }}</span>
              <span v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
              <template v-slot:input>
                <v-text-field
                  v-model="itemClone.url"
                  label="Url"
                  single-line
                />
              </template>
            </v-edit-dialog>
          </v-row>
        </v-col>
      </template>

      <template v-slot:item.order="{item}">
        <v-edit-dialog
          @save="updateItem(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <span class="font-weight-bold">{{ priorityObjectName(item.order) }}</span>
          <template v-slot:input>
            <div
              class="ma-2"
            >
              <DataList
                :item-model.sync="itemClone.order"
                :type-object="$constants.DATA_LISTS.MEDIA_PRIORITIES_LIST"
                label="display order"
              />
            </div>
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="mt-9">
          <v-btn
            v-if="enableMove"
            icon
            color="red"
            @click="openDialog(
              'moveImageDialog',
              { id: item.id })
            "
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-arrow-right-bold
                </v-icon>
              </template>
              Move image
            </v-tooltip>
          </v-btn>

          <v-btn
              v-if="!item.is_archive"
              icon
              @click="setArchiveVideo(item, 1)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on">
                  mdi-archive-arrow-down
                </v-icon>
              </template>
              To Archive
            </v-tooltip>
          </v-btn>
          <v-btn
              v-if="item.is_archive"
              icon
              @click="setArchiveVideo(item, 0)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on">
                  mdi-archive-arrow-up
                </v-icon>
              </template>
              From Archive
            </v-tooltip>
          </v-btn>

          <v-btn
            icon
            @click="deleteImage(item.id)" >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <slot>
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    color="red"
                  >
                    mdi-delete
                  </v-icon>
                </slot>
              </template>
              delete image
            </v-tooltip>
          </v-btn>
        </div>
      </template>

    </v-data-table>
  </v-container>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import ImageWithBorderDialog from '@/components/dialogs/ImageWithBorderDialog.vue';
import DataList from './DataLists/DataList.vue';
import ConfirmDialog from './dialogs/ConfirmDialog.vue';
import manageDialogsMixin from '../mixins/manageDialogsMixin';
import fetchErrorMixin from '../mixins/fetchErrorMixin';

export default {
  name: 'MediaImagesTable',
  components: { ImageWithBorderDialog, ConfirmDialog, DataList },
  mixins: [
    manageDialogsMixin,
    fetchErrorMixin,
  ],
  props: {
    loading: Boolean,
    items: [],
    enableMove: {
      type: Boolean,
      default: true,
    },
    objectId: {
      type: Number,
    },
    path: {
      type: String,
    },
    showArchive: {
      type: Boolean,
    },
  },
  data() {
    return {
      itemClone: {},
      sortBy: 'order',
      sortDesc: false,
      headers: [
        { text: 'ID', value: 'id', width: '30px' },
        { text: 'Preview', value: 'preview', sortable: false },
        { text: 'Options', value: 'options', sortable: false },
        { text: 'Order', value: 'order', width: '120px' },
        {
          text: '',
          value: 'actions',
          sortable: false,
          width: '50px',
        },
      ],
      priorities: {},

      dialogs: {
        newAuthor: {
          isOpened: false,
          data: {
            name: null,
            attribute: null,
            url: null,
          },
          defaults: {
            name: null,
            attribute: null,
            url: null,
          },
        },
        moveImageDialog: {
          isOpened: false,
          data: {
            id: null,
            newObjectId: null,
          },
          defaults: {
            id: null,
            newObjectId: null,
          },
        },
      },
    };
  },

  created() {
    this.priorities = this.listItem(this.$constants.DATA_LISTS.MEDIA_PRIORITIES_LIST.type);
    this.updateList({ ...this.$constants.DATA_LISTS.MEDIA_PRIORITIES_LIST, withOverlay: true });
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
      updateList: 'datalists/updateList',
      forceUpdateList: 'datalists/forceUpdateList',
    }),

    updateItem(itemClone, item) {
      this.$requestHelpers
        .patch(`api/admin/${this.path}/${itemClone.id}`, itemClone)
        .then((response) => {
          if (response.data.success) {
            this.setSnack({
              color: 'green',
              text: 'saved',
            });
          }

          Object.assign(item, response.data.result);
        });
    },

    setArchiveVideo(item, isArchive) {
      const itemClone = {
        id: item.id,
        is_archive: isArchive,
      };
      return this.updateItem(itemClone, item);
    },

    deleteImage(imageId) {
      this.$confirm('Are you sure you want to delete this image?').then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/${this.path}/${imageId}`)
          .then((response) => {
            if (response.data.success) {
              this.itemsComputed = this.itemsComputed
                .filter((el) => response.data.result.map((r) => r.id).includes(el.id));
            }
          }).catch((error) => {
            this.$emit('error', this.fetchError(error));
          });
      });
      return imageId;
    },

    moveImage() {
      this.$requestHelpers
        .post(`api/admin/${this.path}/move/${this.dialogs.moveImageDialog.data.id}`, {
          newObjectId: this.dialogs.moveImageDialog.data.newObjectId,
        })
        .then((response) => {
          if (response.data.success) {
            const items = this.items
              .filter((image) => image.id !== this.dialogs.moveImageDialog.data.id);
            this.$emit('update:items', items);
          }

          this.closeDialog('moveImageDialog');
        });
    },

    createNewAuthor() {
      this.$requestHelpers
        .post(
          'api/admin/author',
          {
            name: this.getDialogData('newAuthor', 'name'),
            attribute: this.getDialogData('newAuthor', 'attribute'),
            url: this.getDialogData('newAuthor', 'url'),
          },
        ).then(() => {
          this.forceUpdateList({ ...this.$constants.DATA_LISTS.AUTHOR_LIST, withOverlay: true });
          this.closeDialog('newAuthor');
        });
    },
  },

  computed: {
    ...mapGetters({
      listItem: 'datalists/listItem',
    }),

    priorityObjectName() {
      return (priorityId) => {
        const priority = this.priorities.list.find((item) => item.id === priorityId);
        return priority === undefined ? 'empty' : `${priority.name}`;
      };
    },

    itemsComputed: {
      get() {
        return this.items.filter((el) => el.is_archive === (this.showArchive ? 1 : 0));
      },
      set(value) {
        this.$emit('update:items', value);
      },
    },
  },
};

</script>
<style scoped>
</style>
